/**
 * №23 Stage C (part 2) — Key Dates as declared data (D-MS2 rev, D-MS9).
 *
 * A date type IS a declared `date`/`datetime` field on the anchor type — there
 * is no registry (№43's `deriveDateTypes()` reads exactly that set, gated in
 * CI; see `src/engine/wf/validate.ts`). BC's two-stage escalation window
 * (`Key Date Attn. Calc.` / `Key Date Urgent Calc.`, per-type DateFormulas,
 * `MatterXDateType.Table.al:36,47`) lands as two optional properties on the
 * field declaration itself — `attention_days` / `urgent_days`
 * (`src/modules/core/definition.ts`, `console/src/lib/types.ts`) — never a
 * second table.
 *
 * The offset is a plain non-negative "days before" count, not a signed
 * DateFormula: the same substitution `wf/dates.ts` (D-WF3 s4) already made for
 * step due-date offsets ("Kuvio has no BC DateFormula type … a signed integer
 * of CALENDAR days"), simplified further because an escalation window only
 * ever counts backwards from the date — there is no "Attention after the
 * date" case to represent, so no sign is needed.
 *
 * Pure and side-effect-free — computed at read, on every render, never stored
 * (G2). `RecordView.tsx` (the table) and `MatterCockpitPage.tsx` (the crumb
 * badge, via `mostUrgentKeyDate`) are the callers today.
 *
 * **R7 ③ grain (ratified 15 Aug, `decisions-log.md`):** "absent, not empty" is
 * a SECTION-grain rule, never a row-grain one. `keyDateState` returning `null`
 * for an empty value is a signal, not a directive — the CALLER decides what
 * that means. `RecordView.tsx`'s table still renders the row (D-W2: a field
 * on a rendered record surface is directly modifiable, so it can never
 * disappear when empty — a matter with no settlement date needs a way to
 * *acquire* one). Only `mostUrgentKeyDate` — which exists to headline a single
 * actionable escalation, not to enumerate every declared date — treats a null
 * result as "not a candidate".
 */

export type KeyDateState = "urgent" | "attention" | "passed" | "none";

export interface KeyDateResult {
  state: KeyDateState;
  /** "N day"/"N days" — the count driving urgent/attention. Empty otherwise. */
  note: string;
  /** Calendar days from `today` to the date — populated for urgent/attention
   *  only (the count `note` renders), so callers can rank several dates by
   *  urgency without re-parsing `note`. */
  daysUntil?: number;
}

/** The leading 'YYYY-MM-DD' of an ISO date/datetime string, as a UTC calendar
 *  day (midnight UTC) — so day-arithmetic never drifts with either side's
 *  timezone or time-of-day. Returns null for anything that doesn't parse. */
function calendarDayUtc(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const [, y, mo, d] = m as unknown as [string, string, string, string];
  const ms = Date.UTC(Number(y), Number(mo) - 1, Number(d));
  return Number.isNaN(ms) ? null : ms;
}

function pluralDays(n: number): string {
  return `${n} day${n === 1 ? "" : "s"}`;
}

/**
 * The state of one declared date field, relative to `today` (defaults to
 * now — frozen under `context.clock.install` in snapshot tests, so this stays
 * deterministic there).
 *
 * Returns `null` when the field has no value, or the value doesn't parse —
 * "there is nothing to compute a state from", not "render nothing". What a
 * `null` means is the caller's call (see the grain note above `KeyDateState`).
 *
 * No window declared (`attentionDays`/`urgentDays` both absent) → always
 * `"none"`, regardless of whether the date itself is in the past. An
 * informational date (e.g. `opened_date`) is not a deadline that can be
 * "Passed" — only a field carrying a real escalation window can pass one.
 */
export function keyDateState(
  dateValue: unknown,
  attentionDays: number | undefined,
  urgentDays: number | undefined,
  today: Date = new Date(),
): KeyDateResult | null {
  if (typeof dateValue !== "string" || dateValue.trim() === "") return null;
  const dateMs = calendarDayUtc(dateValue);
  if (dateMs == null) return null;
  const todayMs = calendarDayUtc(today.toISOString());
  if (todayMs == null) return null;

  if (attentionDays == null && urgentDays == null) return { state: "none", note: "" };

  // date − today, in whole calendar days. Negative once the date is behind us.
  const daysUntil = Math.round((dateMs - todayMs) / 86_400_000);
  if (daysUntil < 0) return { state: "passed", note: "" };
  if (urgentDays != null && daysUntil <= urgentDays) return { state: "urgent", note: pluralDays(daysUntil), daysUntil };
  if (attentionDays != null && daysUntil <= attentionDays) return { state: "attention", note: pluralDays(daysUntil), daysUntil };
  return { state: "none", note: "" };
}

/** Badge class + label for a computed state — the three-column Key Dates
 *  table's "State" cell (`d-full-{light,dark}.png`, the locked reference).
 *  Classes are shipped `app-chrome.css` badge modifiers; `badge--danger` is
 *  new (this stage) but follows the exact `color-mix(... 16%, transparent)`
 *  pattern `badge--oob`/`badge--tm`/`badge--fixed` already use. */
export function keyDateBadge(r: KeyDateResult): { cls: string; label: string } {
  switch (r.state) {
    case "urgent":
      return { cls: "badge badge--danger", label: `Urgent · ${r.note}` };
    case "attention":
      return { cls: "badge badge--oob", label: `Attention · ${r.note}` };
    case "passed":
      return { cls: "badge badge--closed", label: "Passed" };
    default:
      return { cls: "badge badge--soft", label: "—" };
  }
}

export interface KeyDateField {
  /** Declared field name (e.g. "settlement_date") — the caller derives its
   *  own display label from this; this module has no opinion on labels. */
  name: string;
  value: unknown;
  attentionDays?: number;
  urgentDays?: number;
}

export interface MostUrgentDate {
  name: string;
  state: "urgent" | "attention";
  /** "N day"/"N days" — carried through in case a caller wants it; the crumb
   *  badge doesn't use it (the date itself is shown alongside). */
  note: string;
}

/**
 * David's ruling (15 Aug, on the recordcap crumb badge): "most urgent date
 * wins, nothing renders when no date is in a window." Across every declared
 * date field on the anchor, pick the ONE that should headline a crumb-line
 * badge — urgent outranks attention outright; among dates tied on state, the
 * soonest (smallest `daysUntil`) wins, because that is the more urgent one.
 *
 * Fields with no value, no window, or state "none"/"passed" are not
 * candidates: a crumb badge exists to surface something that needs attention
 * *now* — a closed date is what the Key Dates table's "Passed" row is for,
 * and an informational date never had a window to begin with. Returns `null`
 * when nothing qualifies (the badge doesn't render).
 */
export function mostUrgentKeyDate(
  fields: readonly KeyDateField[],
  today: Date = new Date(),
): MostUrgentDate | null {
  let best: (MostUrgentDate & { daysUntil: number }) | null = null;
  const rank: Record<"urgent" | "attention", number> = { urgent: 0, attention: 1 };
  for (const f of fields) {
    const result = keyDateState(f.value, f.attentionDays, f.urgentDays, today);
    if (!result || result.daysUntil == null) continue;
    if (result.state !== "urgent" && result.state !== "attention") continue;
    const candidate = { name: f.name, state: result.state, note: result.note, daysUntil: result.daysUntil };
    if (
      !best ||
      rank[candidate.state] < rank[best.state] ||
      (rank[candidate.state] === rank[best.state] && candidate.daysUntil < best.daysUntil)
    ) {
      best = candidate;
    }
  }
  return best ? { name: best.name, state: best.state, note: best.note } : null;
}
